import * as React from 'react';
import { forEach, get, isEqual, isNil } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useTranslation } from '@lib/useTypedTranslation';
import { usePathBasedQueryString } from '@lib/usePathBasedQueryString';

import { DataLoading } from '../../components/loading/dataLoading';
import { BatteryDevicesService, IDeviceWithDischargeStatus, getNegateParams } from '../../services/shared/device/batteryDevicesService';
import { VIEW, ISortResult } from '../../services/shared/common';
import { getGroups, getManufacturers, getModels, IGroups, getHomeLocations, IHomeLocation } from '../../services/config/config';
import { DeviceDetailsCard } from './compositions/deviceDetailsCard';
import { PaginationBar } from '../../components/data-table/pagination/pagination';
import { Loading } from '../../components/loading/loading';
import ETableHeader from '../../components/table/header';
import Header from '../../components/listCard/header';
import { StatusText } from '../../components/table/statusText';
import { ListCategory } from '../battery-centric';
import { InitialTableState, useTableReducer } from '../../components/data-table/lib';
import { ITableColumn, SortDirection } from '../../components/data-table/types';
import { FilterStatus, IFilterStatus } from '../../components/filters/newFilters/filterStatus';
import { IFilterOption, IFilterResult, convertFilterToQuery, getFilterOptionDisplay, getFilterOptionId } from '../../components/filters/filterLib';
import { DataTable } from '../../components/data-table/dataTable';
import { formatDateLastUpdated, formatStatus, formatValue, getFormatPercentageToLocale, getFormatSecondsToHoursToLocale } from '../../components/data-table/dataFormatters';
import { useUserSettingsContext } from '../../context/userSettings';
import { RequestInitWithRetry } from '../../lib/request';
import { useWorldRequest } from '../../lib/useWorldRequest';
import { useWorldAction } from '../../lib/useWorldAction';
import { useLanguageAndLocaleContext } from '../../context/languageAndLocale';

import './styles.css';
import { ITranslationKeys } from 'components/i18n/keys';
import classNames from 'classnames';
import { FilterContext } from '@components/data-table/filters/context';
import { GroupListFilter, ManufacturerListFilter, ModelListFilter, HomeLocationsListFilter } from '../../containers/core/devices/compositions/filters/devicesListFilters';
import { DeviceAlertsFilter } from '@components/data-table/filters/deviceAlertsFilter';
import { useDeviceIdentifier } from '@lib/useDeviceIdentifier';
import { rearrangeList } from '@lib/rearrangeList';
import { getNewStatusFilters, handleNewFilterRemoved as handleNewFilterRemovedUtil } from '../battery-centric/filterUtils';

export const AppContext = React.createContext({
  setSelectedItem: null,
  getSelectedItem: null
});

export const tableName = 'batteryDevicesList';
export const defaultPageSize = 20;
const service = new BatteryDevicesService();

export const getBatteryDevicesColumnFromFilterGroup = (filterGroup: string): string => {
  switch (filterGroup) {
  case 'groups':
    return 'groups';
  case 'manufacturers':
    return 'manufacturer';
  case 'models':
    return 'model';
  case 'homeLocations':
    return 'homeLocation';
  case 'status':
    return 'status';
  default:
    throw new Error('Unknown filter group: ' + filterGroup);
  }
};

export function convertQueryStringToDeviceListFilter(queryString: string, t: any): IFilterResult | undefined {
  const params = new URLSearchParams(queryString);
  const filter: IFilterResult = {};
  let hasFilters = false;

  params.forEach((value, key) => {
    const values = value.split(',').filter(Boolean);
    if (values.length === 0) return;

    if (key === 'groups') {
      filter[key] = values.map((group) => {
        if (group === '\\0' || group === 'no-group') {
          return { id: 'no-group', value: t('NO_GROUP_title'), nullFilter: true };
        }
        return group;
      });
      hasFilters = true;
    } else if (key === 'manufacturers') {
      filter[key] = values;
      hasFilters = true;
    } else if (key === 'models') {
      filter[key] = values;
      hasFilters = true;
    } else if (key === 'status') {
      const statusMap: { [key: string]: string } = {
        'red': 'PROBLEM',
        'yellow': 'WARNING',
        'green': 'GOOD'
      };
      filter[key] = values.map(id => {
        const displayKey = statusMap[id];
        return displayKey ? { id, value: t(displayKey) } : undefined;
      }).filter(Boolean);
      hasFilters = true;
    }
  });

  return hasFilters ? filter : undefined;
}

export function getFilterGroupDisplayName(t: any) {
  return (filterGroup: string, count: number): string => {
    switch (filterGroup) {
    case 'status':
      return t('AVERAGE_DISCHARGE_STATUS_HEADER', { ns: 'batteryEssentialsDeviceView' });
    case 'manufacturers':
      return t('MANUFACTURER', { count, ns: 'filterStatus' });
    case 'models':
      return t('MODEL', { count, ns: 'filterStatus' });
    case 'groups':
      return t('GROUP', { count, ns: 'filterStatus' });
    case 'homeLocations':
      return t('HOME_LOCATION', { count, ns: 'filterStatus' });
    default:
      return filterGroup;
    }
  };
}

interface Props {
  selectedId: string,
  selectedTimestamp: number
}

// DataTableHeader always calls onSort with a defined numeric column index (never nil), but its
// direction can legitimately be nil (clicking a third time clears the sort). This defaults both
// defensively so a cleared/omitted value is always reported as an explicit 0/null rather than
// undefined.
export function resolveSortArgs(column: number, direction: SortDirection): [number, SortDirection] {
  return [!isNil(column) ? column : 0, !isNil(direction) ? direction : null];
}

const initialTableSortField: string = 'updated';
const initialTableSortColumn: number = 8;
const initialTableSortDirection: SortDirection = 'desc';
const initialSort = { column: initialTableSortColumn, direction: initialTableSortDirection, field: initialTableSortField };

export function BatteryDevicesContainerBase(props: Props) {
  const ns = 'translation';
  const { t } = useTranslation(ns);
  const { locale } = useLanguageAndLocaleContext();

  const { queryString, prevQueryString } = usePathBasedQueryString(/\/core\/batteries\/devices/);

  const { tablePageSizes } = useUserSettingsContext();
  const deviceIdentifier = useDeviceIdentifier();

  const [selectedId, setSelectedId] = useState(props.selectedId || '-');
  const [idUpdateTimestamp, setIdUpdateTimestamp] = useState(props.selectedTimestamp || 0);
  const [listResetTimestamp, setListResetTimestamp] = useState<number>(undefined);
  const [currentFilter, setCurrentFilter] = useState(() => convertQueryStringToDeviceListFilter(queryString, t));
  const [statusMessage, setStatusMessage] = useState('DEVICE');
  const [resultStatus, setResultStatus] = useState<boolean>(false);
  const [initialRenderComplete, setInitialRenderComplete] = useState<boolean>(false);
  const [availableFiltersResponse, setAvailableFiltersResponse] = useState<{groups: string[], manufacturers: string[], models: string[], homeLocations: IHomeLocation[]}>(undefined);
  const prevSelectedIdRef = useRef<string | undefined>(props.selectedId);


  const doGetList = useWorldAction(service.getList);
  const doGetDeviceEstateDataWithFilters = useWorldAction(service.getDeviceEstateDataWithFilters);
  const doGetDeviceDetails = useWorldAction(service.getDeviceDetails);
  const doExportList = useWorldAction(service.exportList);
  const batteryCentricDevicesDetails = classNames(
    'batteryCentric_devices_details',
    'col-md-12',
    'col-sm-12',
    'mt-sm-2',
    'mt-2',
    'mt-lg-0',
    "col-xl-12 col-lg-12"
  );

  const initialTableState: InitialTableState = {
    initialRows: 4,
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: initialSort
  };

  async function loadDetails(): Promise<any> {
    if (selectedId === '-') {
      return '-';
    }
    return doGetDeviceDetails(selectedId);
  }

  const [
    { offset, limit, total, sort, dataRequestId, data, isLoading, search, selectedRow },
    { onSort, onPageChange, onPageSizeChange, onDataLoaded, onSearch, onRowSelected, onLoading }
  ] = useTableReducer<IDeviceWithDischargeStatus>(tableName, initialTableState);

  async function exportList(): Promise<string> {
    const filters = convertFilterToQuery(currentFilter);
    return await doExportList({ statusType: 'all', limit: 10000, offset, search, groups: get(filters, 'groups') as string[], manufacturers: get(filters, 'manufacturers') as string[], models: get(filters, 'models') as string[], homeLocations: get(filters, 'homeLocations') as string[], status: get(filters, 'status') as string[], ...getNegateParams(filters) });
  }

  const handleFilterRemoved = (id: string, item: IFilterStatus) => {
    handleNewFilterRemovedUtil(currentFilter, id, item, setCurrentFilter);
  };

  const handleClearFilters = () => {
    setCurrentFilter(undefined);
  };

  const handleSort = (column: number, direction: SortDirection, info: ITableColumn) => {
    const [resolvedColumn, resolvedDirection] = resolveSortArgs(column, direction);
    onSort(resolvedColumn, resolvedDirection, info);
  };

  const handleRowSelected = (rowIndex: number) => {
    const rowDataId = get(data[rowIndex], "id");
    if (rowIndex !== selectedRow) {
      setSelectedId(rowDataId);
      onRowSelected(rowIndex);
    }
  };

  const pinned = React.useMemo(() => {
    let pinned;
    const sortSet = (sort && !isEqual(sort, initialSort)) ? true : false;
    const filtersSet = (currentFilter && (Array.isArray(currentFilter?.groups) && currentFilter.groups.length > 0 || Array.isArray(currentFilter?.manufacturers) && currentFilter.manufacturers.length > 0 || Array.isArray(currentFilter?.models) && currentFilter.models.length > 0 || Array.isArray(currentFilter?.homeLocations) && currentFilter.homeLocations.length > 0 || Array.isArray(currentFilter?.status) && currentFilter.status.length > 0)) ? true : false;
    if (!isNil(props.selectedId)) {
      pinned = (!search && !sortSet && !filtersSet) ? props.selectedId : undefined;
    }
    return pinned;
  }, [currentFilter, props.selectedId, search, sort]);

  const [availableFiltersDataFetcher, onAvailableFiltersFetched] = useMemo(() => {
    return [
      () => (options: RequestInitWithRetry) => {
        return Promise.all([
          getGroups()(options),
          getManufacturers()(options),
          getModels()(options),
          getHomeLocations()(options)
        ]);
      },
      ([groupsRaw, manufacturersRaw, modelsRaw, homeLocationsRaw]: [IGroups, string[], string[], IHomeLocation[]]) => {
        const groups = get(groupsRaw, 'groups.nodes', []).map((o: any) => o.name);
        setInitialRenderComplete(true);
        setAvailableFiltersResponse({ groups, manufacturers: manufacturersRaw, models: modelsRaw, homeLocations: homeLocationsRaw });
      }
    ];
  }, []);

  useWorldRequest(availableFiltersDataFetcher, { onSuccess: onAvailableFiltersFetched });

  useEffect(() => {
    const newFilter = convertQueryStringToDeviceListFilter(queryString, t);
    if (prevQueryString !== queryString && !isEqual(currentFilter, newFilter)) {
      setCurrentFilter(newFilter);
    }
  }, [queryString, prevQueryString, currentFilter, t]);

  useEffect(() => {
    let selectedIndex = -1;
    if (!isLoading) {
      selectedIndex = data.findIndex(battery => battery?.id === selectedId);
    }
    if (props.selectedId && isNil(selectedRow) && !isLoading) {
      setSelectedId(props.selectedId);
      selectedIndex = 0;
    }
    if (selectedIndex !== selectedRow && !isLoading) {
      onRowSelected(selectedIndex);
    }
  }, [onRowSelected, selectedRow, isLoading, data, selectedId, props.selectedId]);

  useEffect(() => {
    if (search) {
      setStatusMessage('SEARCH_MATCH_STATEMENT');
      setResultStatus(true);
    } else {
      setStatusMessage('DEVICE');
      setResultStatus(false);
    }
  }, [search, t, total]);

  useEffect(() => {
    if (props.selectedTimestamp && idUpdateTimestamp !== props.selectedTimestamp) {
      setIdUpdateTimestamp(props.selectedTimestamp);
      setListResetTimestamp(props.selectedTimestamp);
    }
  }, [idUpdateTimestamp, props.selectedTimestamp, selectedId]);

  useEffect(() => {
    onLoading();
  }, [onLoading, props.selectedId, props.selectedTimestamp, currentFilter]);

  useEffect(() => {
    // Only clear filter and search when selectedId actually changes, not on initial mount
    if (prevSelectedIdRef.current !== props.selectedId) {
      onSearch(undefined);
      setCurrentFilter(undefined);
      prevSelectedIdRef.current = props.selectedId;
    }
  }, [props.selectedId, onSearch]);

  useEffect(() => {
    async function getList(pinnedId: string, statusType: string, search: string, limit: number, offset: number, filters: any, sort: ISortResult): Promise<any[]> {
      const convertedFilters = convertFilterToQuery(filters);
      return doGetList({ statusType: statusType as VIEW, limit, offset, pinnedId: pinnedId, search, sort, groups: get(convertedFilters, 'groups') as string[], manufacturers: get(convertedFilters, 'manufacturers') as string[], models: get(convertedFilters, 'models') as string[], homeLocations: get(convertedFilters, 'homeLocations') as string[], status: get(convertedFilters, 'status') as string[], ...getNegateParams(convertedFilters) });
    }

    async function getCount(search: string, filters: any): Promise<number> {
      const convertedFilters = convertFilterToQuery(filters);
      return await doGetDeviceEstateDataWithFilters(search, convertedFilters as any);
    }

    if (isLoading && initialRenderComplete) {
      Promise.all([
        getList(pinned, 'all', search, limit, offset, currentFilter, { field: sort.field, order: sort.direction }),
        getCount(search, currentFilter)
      ]).then(([list, count]) => {
        onDataLoaded(list, count, dataRequestId);
      });
    }
  }, [onDataLoaded, currentFilter, dataRequestId, isLoading, limit, offset, pinned, search, sort, initialRenderComplete, doGetList, doGetDeviceEstateDataWithFilters]);

  const { path } = useRouteMatch();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // following condition handles devices linked to deleted batteries; this container only
    // mounts inside the Core Batteries hub now, so the hub's devices route is the only path
    // pattern under which a click-through id can arrive
    if (params.has('id') && !props.selectedId && path.includes('/core/batteries/devices')) {
      setSelectedId('-');
    }
  }, [props.selectedId, path, location.search]);


  const filterStatus = <FilterStatus
    selectedFilters={getNewStatusFilters(currentFilter)}
    onFilterRemoved={handleFilterRemoved}
    onClearFilters={handleClearFilters}
  />;

  const columns = React.useMemo(() => {
    function getHomeLocationName(value: string, rowData: IDeviceWithDischargeStatus) {
      return rowData.homeLocation ? formatValue(rowData.homeLocation.name) : "-";
    }

    const baseColumns: ITableColumn[] = [
      { id: 'serialNumber', title: t('SERIAL_NUMBER'), dataPath: 'serialNumber', sortable: true, processor: formatValue },
      { id: 'imei', title: 'IMEI', dataPath: 'imei', sortable: false, processor: formatValue, minWidth: '10rem' },
      { id: 'assetTag', title: t('ASSET_TAG'), dataPath: 'assetTag', sortable: true, processor: formatValue, minWidth: '10rem' },
      { id: 'deviceName', title: t('DEVICE_NAME'), dataPath: 'deviceName', sortable: true, processor: formatValue },
      { id: 'discharge', title: t('AVERAGE_DISCHARGE_RATE', { ns: 'batteryEssentialsDeviceView' }), dataPath: 'averageDischarge', sortable: true, processor: getFormatPercentageToLocale(locale, 2), textAlign: 'right' },
      { id: 'averageDischargeTime', title: t('AVERAGE_DISCHARGE_TIME', { ns: 'translation' }), dataPath: 'averageDischargeTime', sortable: true, processor: getFormatSecondsToHoursToLocale(locale), textAlign: 'right' },
      { id: 'manufacturer', title: t('MANUFACTURER'), dataPath: 'manufacturer', sortable: true, processor: formatValue, FilterForm: ManufacturerListFilter },
      { id: 'model', title: t('MODEL'), dataPath: 'model', sortable: true, processor: formatValue, FilterForm: ModelListFilter, minWidth: '8rem' },
      { id: 'lastSeen', title: t('LAST_SEEN'), dataPath: 'updated', sortable: true, processor: formatDateLastUpdated, minWidth: '8rem' },
      { id: 'groups', title: t('GROUPS'), dataPath: 'group', sortable: false, processor: formatValue, FilterForm: GroupListFilter, minWidth: '10rem' },
      { id: 'homeLocation', title: t('HOME_LOCATION'), dataPath: 'homeLocation.id', sortable: true, processor: getHomeLocationName, FilterForm: HomeLocationsListFilter },
      { id: 'status', title: t('STATUS'), dataPath: 'averageDischargeStatus', sortable: false, processor: formatStatus, textAlign: 'center', FilterForm: DeviceAlertsFilter }
    ];

    const updatedColumn: ITableColumn[] = rearrangeList(baseColumns, deviceIdentifier.id);

    return updatedColumn;
  }, [deviceIdentifier, t, locale]);

  const list = <DataTable
    data={data}
    columns={columns}
    className={isLoading ? '' : 'data-ready'}
    sortDirection={sort.direction}
    sortedColumnIndex={sort.column}
    options={{ rowIdDataPath: 'id' }}
    onSort={(column, direction, info) => handleSort(column, direction, info)}
    onRowSelected={handleRowSelected}
    selectedRow={selectedRow}
  />;

  const headerTitle = t('All_DEVICES_TITLE', { ns: 'batteryEssentialsDeviceView' });

  const isDeviceListData = !isLoading && data?.length > 0;

  const availableFilters = useMemo(() => {
    const nullGroupFilter = { id: 'no-group', value: t('NO_GROUP_title'), nullFilter: true };
    const nullHomeLocationFilter = { id: 'no-homelocation', value: t('NO_HOME_LOCATION'), nullFilter: true };
    const statusFilterValues = [
      { id: 'red', value: t('PROBLEM') },
      { id: 'yellow', value: t('WARNING') },
      { id: 'green', value: t('GOOD') }
    ];
    return {
      groups: availableFiltersResponse?.groups ? [nullGroupFilter, ...availableFiltersResponse.groups] : [],
      manufacturers: availableFiltersResponse?.manufacturers || [],
      models: availableFiltersResponse?.models || [],
      homeLocations: availableFiltersResponse?.homeLocations
        ? [nullHomeLocationFilter, ...availableFiltersResponse.homeLocations.map((o) => ({ id: o.id, value: o.name }))]
        : [],
      status: statusFilterValues
    };
  }, [availableFiltersResponse, t]);

  const caseCheckedStatusMessage = statusMessage === 'DEVICE' ? t(statusMessage, { count: total }).toLowerCase() : t(statusMessage as keyof ITranslationKeys['translation'], { count: total });
  return (<FilterContext.Provider value={{
    getColumnFromFilterGroup: getBatteryDevicesColumnFromFilterGroup,
    getFilterGroupDisplayName: getFilterGroupDisplayName(t),
    filters: currentFilter || {},
    handleFiltersUpdated: setCurrentFilter,
    filterOptions: {
      availableFilters,
      filtersLoaded: initialRenderComplete
    },
    handleCancel: () => {}
  }}>
    <div className="batteryEssentialsTable_devices_container" >
      <div className="row">
        <div className={batteryCentricDevicesDetails}>
          <DataLoading key={`${selectedId}:${idUpdateTimestamp}`} loadData={loadDetails}>
            <DeviceDetailsCard id={selectedId} data={null} />
          </DataLoading>
        </div>
      </div>
      <div className="row batteryEssentialsTable_container deviceCentric" key={listResetTimestamp}>
        <div className="col-md-12">
          <div className="eTable-no-categories">
            <ETableHeader>
              <Header
                categories={[]}
                onExportClicked={exportList}
                exportFileName='Devices.csv'
                exportButtonDisabled={!isDeviceListData}
                onSearchClicked={(input) => onSearch(input)}
                headerTitle={headerTitle}
                hideCategories={true} />
            </ETableHeader>
            <div className="statusText" style={{ display: currentFilter && !isEqual(currentFilter, {}) ? 'block' : 'none' }}>
              {filterStatus}
            </div>
            <Loading isLoading={isLoading} transparentOverlay={false}>
              <div className='eTable_list'>
                {list}
                <PaginationBar
                  offset={offset}
                  limit={limit}
                  total={total}
                  onNewPage={(newOffset) => onPageChange(newOffset)}
                  onNewLimit={(newLimit) => onPageSizeChange(newLimit)}
                >
                  <StatusText
                    searchString={search}
                    total={total}
                    offset={offset}
                    limit={limit}
                    item={caseCheckedStatusMessage}
                    fullStatusText={resultStatus}
                  />
                </PaginationBar>
              </div>
            </Loading>
          </div>
        </div>
      </div>
    </div>
  </FilterContext.Provider>);
}

export const BatteryDevicesContainer = BatteryDevicesContainerBase;
